import { matchPath } from "react-router";
import { isLoading, Loading } from "../loading";
import { useAllTaskTypes } from "./useTaskTypes";
import { useRemoveMediaArticlesAfterComplete } from "./useRemoveMediaArticlesAfterComplete";
import { Task } from "../types/Task";
import { useQueryClient } from "@tanstack/react-query";
import { resetVotesAfterComplete } from "./useResetVotesAfterComplete";
import { useRedirect } from "./useRedirect";
import { manageMediaArticlesAfterComplete } from "./manageMediaArticlesAfterComplete";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";

// Routes that carry a :mediaId param, used to figure out which media the
// user was looking at when a "manage-media-articles" task was completed.
const MEDIA_ID_ROUTE_PATTERNS = [
  "/mediamaintenance/tabs/:mediaId",
  "/mediamaintenance/update/:mediaId",
  "/mediamaintenance/medias/:mediaId/articles/:articleId/product_article_id/:productArticleId",
  "/mediamaintenance/:mediaId/articles/:articleId/createtask",
];

const getCurrentMediaId = (pathname: string): string | undefined => {
  for (const pattern of MEDIA_ID_ROUTE_PATTERNS) {
    const match = matchPath(pattern, pathname);
    if (match?.params["mediaId"]) {
      return match.params["mediaId"];
    }
  }
  return undefined;
};

// The article search page isn't scoped to a single media (an article can
// live in many medias), so there's no media tab to return to after
// completing a "manage-media-articles" task from here — navigate to the
// media maintenance home instead.
const MEDIA_SEARCH_ROUTE_PATTERN =
  "/mediamaintenance/articles/search/:productArticleId";

export const isMediaSearchPage = (pathname: string): boolean =>
  matchPath(MEDIA_SEARCH_ROUTE_PATTERN, pathname) !== null;

export const useAfterComplete = (
  task: Record<string, unknown> | undefined | Loading
): (() => void) => {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const countryId = useCountryId();
  const { data: taskTypes, isLoading: isTaskTypesLoading } = useAllTaskTypes();
  const redirect = useRedirect();
  const navigate = useFixaNavigate();
  const { pathname } = useFixaLocation();
  const removeMediaArticlesMutation = useRemoveMediaArticlesAfterComplete();

  const afterComplete = taskTypes?.find(
    (type) => type.id === (task as Task)?.task_type_id
  )?.after_complete;

  if (
    isLoading(task) ||
    isTaskTypesLoading ||
    !afterComplete ||
    task === undefined
  ) {
    return () => {};
  }

  const executeAfterComplete = async () => {
    const { id, props } = afterComplete;

    switch (id) {
      case "redirect":
        redirect(props.url, props.openNewTab);
        return;
      case "reset-votes":
        resetVotesAfterComplete(task, props, queryClient);
        return;
      case "remove-media-articles":
        await removeMediaArticlesMutation.mutateAsync({ task, props });
        return;
      case "manage-media-articles":
        manageMediaArticlesAfterComplete(
          task,
          props,
          queryClient,
          storeId,
          countryId,
          getCurrentMediaId(pathname),
          navigate,
          isMediaSearchPage(pathname)
        );
        return;
      default:
        return;
    }
  };

  return executeAfterComplete;
};
